import { useI18n } from '@/lib/i18n'
import type { EventConfig, SurveyField } from '@/types'
import { SURVEY_FIELD_TYPES } from '@/lib/constants'
import { generateId } from '@/lib/utils'

export default function SurveyBuilder({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const fields = event.survey || []

  const updateFields = (newFields: SurveyField[]) => {
    onChange({ survey: newFields.length > 0 ? newFields : null })
  }

  const addField = (type: typeof SURVEY_FIELD_TYPES[number]) => {
    const field: SurveyField = {
      id: generateId(),
      type,
      label: type === 'gdpr' ? 'I consent to the processing of my data' : `New ${type}`,
      required: false,
      order: fields.length,
    }
    updateFields([...fields, field])
  }

  const updateField = (id: string, updates: Partial<SurveyField>) => {
    updateFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)))
  }

  const deleteField = (id: string) => {
    updateFields(fields.filter((f) => f.id !== id))
  }

  const moveField = (id: string, direction: -1 | 1) => {
    const idx = fields.findIndex((f) => f.id === id)
    const newIdx = idx + direction
    if (newIdx < 0 || newIdx >= fields.length) return
    const copy = [...fields]
    const [moved] = copy.splice(idx, 1)
    copy.splice(newIdx, 0, moved)
    updateFields(copy.map((f, i) => ({ ...f, order: i })))
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-semibold">{t('survey.title')}</h2>

      <div className="flex flex-wrap gap-2">
        {SURVEY_FIELD_TYPES.map((type) => (
          <button
            key={type}
            onClick={() => addField(type)}
            className="rounded-md bg-secondary px-3 py-1.5 text-sm hover:bg-secondary/80"
          >
            + {t(`survey.fieldTypes.${type}`)}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {fields.map((field, idx) => (
          <div key={field.id} className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase text-muted-foreground">{t(`survey.fieldTypes.${field.type}`)}</span>
              <div className="flex gap-1">
                <button onClick={() => moveField(field.id, -1)} disabled={idx === 0} className="rounded px-2 py-1 text-xs bg-muted hover:bg-muted/80 disabled:opacity-30">{t('survey.up')}</button>
                <button onClick={() => moveField(field.id, 1)} disabled={idx === fields.length - 1} className="rounded px-2 py-1 text-xs bg-muted hover:bg-muted/80 disabled:opacity-30">{t('survey.down')}</button>
                <button onClick={() => deleteField(field.id)} className="rounded px-2 py-1 text-xs bg-destructive text-destructive-foreground">{t('survey.delete')}</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">{t('survey.label')}</label>
                <input type="text" value={field.label} onChange={(e) => updateField(field.id, { label: e.target.value })} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t('survey.placeholder')}</label>
                <input type="text" value={field.placeholder || ''} onChange={(e) => updateField(field.id, { placeholder: e.target.value })} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground" />
              </div>
            </div>
            {field.type === 'dropdown' && (
              <div>
                <label className="text-xs text-muted-foreground">{t('survey.options')}</label>
                <input type="text" value={field.options?.join(', ') || ''} onChange={(e) => updateField(field.id, { options: e.target.value.split(',').map((s) => s.trim()) })} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground" />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={field.required} onChange={(e) => updateField(field.id, { required: e.target.checked })} className="h-4 w-4" />
              {t('survey.required')}
            </label>
          </div>
        ))}
      </div>

      {fields.length === 0 && (
        <div className="text-sm text-muted-foreground">{t('survey.noFields')}</div>
      )}
    </div>
  )
}
